import { lenis } from './scroll';

/*
  Prise de rendez-vous Calendly : tout lien vers calendly.com ouvre la popin
  Calendly au lieu de quitter le site. Sans JavaScript, le lien mène
  directement à la page Calendly.

  Le script et la feuille de style de Calendly sont chargés quand le
  navigateur est inactif, pour ne pas ralentir l'affichage, ou au premier
  clic s'ils ne le sont pas encore. S'ils ne se chargent pas, le clic mène à la
  page Calendly dans l'onglet courant.
*/

const WIDGET_URL = 'https://assets.calendly.com/assets/external/widget';
const LINK_SELECTOR = 'a[href^="https://calendly.com/"]';

type Calendly = { initPopupWidget(options: { url: string }): void };

let loading: Promise<Calendly> | undefined;

function loadWidget(): Promise<Calendly> {
  loading ??= new Promise((resolve, reject) => {
    const style = document.createElement('link');
    style.rel = 'stylesheet';
    style.href = `${WIDGET_URL}.css`;
    document.head.append(style);

    const script = document.createElement('script');
    script.src = `${WIDGET_URL}.js`;
    script.async = true;
    script.addEventListener('load', () => {
      const { Calendly } = window as unknown as { Calendly?: Calendly };
      if (Calendly) resolve(Calendly);
      else reject(new Error('Calendly indisponible'));
    });
    script.addEventListener('error', () => reject(new Error('Calendly indisponible')));
    document.head.append(script);
  }).catch((error: unknown) => {
    // nouvelle tentative possible au prochain clic
    loading = undefined;
    throw error;
  }) as Promise<Calendly>;
  return loading;
}

/** Bloque le défilement de la page tant que la popin Calendly est ouverte. */
function lockScrollWhileOpen() {
  lenis?.stop();
  const observer = new MutationObserver(() => {
    if (document.querySelector('.calendly-overlay')) return;
    observer.disconnect();
    lenis?.start();
  });
  observer.observe(document.body, { childList: true, subtree: true });
}

export function initCalendly() {
  if (!document.querySelector(LINK_SELECTOR)) return;

  const preload = () => {
    loadWidget().catch(() => {
      // échec silencieux : le clic retentera, puis suivra le lien
    });
  };
  if ('requestIdleCallback' in window) requestIdleCallback(preload, { timeout: 4000 });
  else setTimeout(preload, 2000);

  // phase de capture : passe avant le rideau et les liens d'ancre
  document.addEventListener(
    'click',
    (event) => {
      const link = (event.target as HTMLElement).closest<HTMLAnchorElement>(LINK_SELECTOR);
      if (
        !link ||
        event.button !== 0 ||
        event.metaKey ||
        event.ctrlKey ||
        event.shiftKey ||
        event.altKey
      )
        return;
      event.preventDefault();
      loadWidget().then(
        (calendly) => {
          calendly.initPopupWidget({ url: link.href });
          lockScrollWhileOpen();
        },
        () => {
          // Calendly indisponible : on suit le lien dans l'onglet courant (une
          // navigation, contrairement à window.open, n'est pas bloquée hors du clic)
          location.assign(link.href);
        },
      );
    },
    true,
  );
}
